import cleanExistingSuffixes from './cleanExistingSuffixes'
import getSuffixForAcabado from './getSuffixForAcabado'

export type AcabadoSkipReason =
  | 'empty-catalog'
  | 'unit-not-eligible'
  | 'already-base'
  | 'already-target'

export type AcabadoUnitDecision =
  | { action: 'apply'; catalogId: string; from: string }
  | { action: 'skip'; reason: AcabadoSkipReason; from: string }

export type AcabadoSuffixToken = '' | 'LA' | 'LI' | 'Ru' | 'R2'

function tokenToAcabadoSuffix(token: string): AcabadoSuffixToken | null {
  const upper = token.toUpperCase()
  if (upper === 'LA') return 'LA'
  if (upper === 'LI') return 'LI'
  if (upper === 'RU' || token === 'Ru') return 'Ru'
  if (upper === 'R2') return 'R2'
  return null
}

/** Detecta orden corrupto: "GO LI" en vez de "LI GO" o solo "GO" */
export function isCorruptCatalogIdOrder(catalogId: string): boolean {
  return /\sGO\s+(LI|LA|Ru|R2)\s*$/i.test(String(catalogId ?? '').trim())
}

export function parseLinkedCatalogParts(catalogId: string): {
  base: string
  acabadoToken: AcabadoSuffixToken
  hasGola: boolean
} {
  const raw = String(catalogId ?? '').trim()
  const base = cleanExistingSuffixes(raw)
  const remainder = raw.slice(base.length).trim()

  let acabadoToken: AcabadoSuffixToken = ''
  let hasGola = false

  for (const token of remainder.split(/\s+/).filter(Boolean)) {
    const upper = token.toUpperCase()
    if (upper === 'GO') {
      hasGola = true
      continue
    }
    const parsed = tokenToAcabadoSuffix(token)
    if (parsed) acabadoToken = parsed
  }

  return { base, acabadoToken, hasGola }
}

export function isGolaCatalogId(catalogId: string): boolean {
  return parseLinkedCatalogParts(catalogId).hasGola
}

export function isLinealCatalogId(catalogId: string): boolean {
  return parseLinkedCatalogParts(catalogId).acabadoToken === 'LI'
}

/** Clave normalizada para sceneGLB (siempre mayúsculas) */
export function sceneGLBLookupKey(catalogId: string): string {
  return String(catalogId ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase()
}

export function isVitrinaCatalogId(catalogId: string): boolean {
  const upper = cleanExistingSuffixes(catalogId).toUpperCase()
  return (
    upper.startsWith('KABV') ||
    upper.startsWith('KAPV') ||
    upper.startsWith('PE KABV') ||
    upper.startsWith('PE KAPV')
  )
}

export function isGenericFrontalMesh(item: {
  catalogID?: string | null
  referencia?: string | null
}): boolean {
  const catalog = String(item.catalogID ?? '').trim()
  const ref = String(item.referencia ?? '').trim()
  return catalog === 'Frontal' || ref === 'Frontal'
}

/** Laterales y unidades fijas sin variantes LA/LI/Ru en catálogo */
export function isFixedCatalogWithoutSuffixVariants(catalogId: string): boolean {
  const base = cleanExistingSuffixes(catalogId).toUpperCase()
  return /^(LC|CB|CC|LA|LB)\d/.test(base) || base.startsWith('PE LC') || base.startsWith('PE CB')
}

export function unitAcceptsSpecialSuffix(catalogId: string): boolean {
  const raw = String(catalogId ?? '').trim()
  if (!raw || isGenericFrontalMesh({ catalogID: raw })) return false
  if (isFixedCatalogWithoutSuffixVariants(raw)) return false

  const hasPE = raw.toUpperCase().startsWith('PE ')
  const base = cleanExistingSuffixes(raw)
  const code = (hasPE ? `PE ${base}` : base).toUpperCase()
  const withoutPE = hasPE ? code.slice(3).trim() : code

  return /^(KBB|KAB|KCB|KCFB|KCHB|KCHMB|KBP|KGB|CC|CB|FA|RC|RA|RB|KB)/.test(withoutPE)
}

export function normalizeAcabadoToken(suffix: string): AcabadoSuffixToken {
  const trimmed = suffix.trim().toUpperCase()
  if (trimmed === 'LA') return 'LA'
  if (trimmed === 'LI') return 'LI'
  if (trimmed === 'RU') return 'Ru'
  if (trimmed === 'R2') return 'R2'
  return ''
}

export function isSpecialGrupoAcabado(acabado: string): boolean {
  return Boolean(normalizeAcabadoToken(getSuffixForAcabado(acabado)))
}

export function buildLinkedCatalogId(options: {
  base: string
  acabadoToken?: AcabadoSuffixToken
  includeGola?: boolean
}): string {
  const { base, acabadoToken = '', includeGola = false } = options
  const cleanBase = cleanExistingSuffixes(base)
  const isLI = acabadoToken === 'LI'
  const effectiveGola = Boolean(includeGola) && !isLI

  let result = cleanBase
  if (acabadoToken) result += ` ${acabadoToken}`
  if (effectiveGola) result += ' GO'

  return result
}

/** GO LI / GO Ru corrupto → reordenar a BASE GO (LI nunca con GO) */
export function getRepairCatalogId(catalogId: string): string | null {
  const raw = String(catalogId ?? '').trim()
  if (!raw) return null

  if (isCorruptCatalogIdOrder(raw)) {
    const { base } = parseLinkedCatalogParts(raw)
    return buildLinkedCatalogId({ base, includeGola: true })
  }

  return null
}

export function normalizeLinkedCatalogId(catalogId: string): string {
  const raw = String(catalogId ?? '').trim()
  if (!raw) return raw

  const repair = getRepairCatalogId(raw)
  if (repair) return repair

  const { base, acabadoToken, hasGola } = parseLinkedCatalogParts(raw)
  return buildLinkedCatalogId({ base, acabadoToken, includeGola: hasGola })
}

/**
 * Tras toggle Gola la API devuelve el par idCambioGolaBlanco (ej. KAB4060 ↔ KAB4260 GO,
 * KBB8060 ↔ KGBB8060 GO). La base viene de la API; el sufijo Ru/LA/R2 se conserva del item.
 * Orden: BASE Ru GO (nunca GO Ru).
 */
export function resolveCatalogIdAfterGolaChange(
  previousCatalogId: string,
  apiCodigo: string,
): string {
  const prev = parseLinkedCatalogParts(previousCatalogId)
  const api = parseLinkedCatalogParts(apiCodigo)

  const golaToggled = prev.hasGola !== api.hasGola

  // Toggle gola: la API define el par correcto (KAB4060→KAB4260 GO, KBB→KGBB, etc.)
  const base = golaToggled ? api.base : prev.base

  let acabadoToken = prev.acabadoToken || api.acabadoToken
  let includeGola = api.hasGola

  if (includeGola && acabadoToken === 'LI') {
    acabadoToken = ''
  }

  if (isVitrinaCatalogId(base)) {
    acabadoToken = ''
    includeGola = api.hasGola
  }

  return buildLinkedCatalogId({ base, acabadoToken, includeGola })
}

/**
 * Tras fetch por idCambio* con el mueble en modo Gola: la API devuelve codigo sin GO.
 * Conserva base Gola (mesh) y GO; aplica el sufijo de acabado de la API.
 */
export function resolveCatalogIdPreservingGolaForAcabadoChange(
  previousCatalogId: string,
  apiCodigo: string,
): string {
  const prev = parseLinkedCatalogParts(previousCatalogId)
  const api = parseLinkedCatalogParts(apiCodigo)

  if (!prev.hasGola) {
    return buildLinkedCatalogId({
      base: api.base,
      acabadoToken: api.acabadoToken,
      includeGola: api.hasGola,
    })
  }

  let acabadoToken = api.acabadoToken || prev.acabadoToken
  if (acabadoToken === 'LI') acabadoToken = ''

  return buildLinkedCatalogId({
    base: prev.base,
    acabadoToken,
    includeGola: true,
  })
}

/** catalogID efectivo para resolver cambios de acabado (incluye GO si golaActiva). */
export function effectiveCatalogIdForAcabadoChange(item: {
  catalogID?: string | null
  golaActiva?: boolean | null
}): string {
  const raw = String(item.catalogID ?? '').trim()
  if (!raw) return raw

  const parsed = parseLinkedCatalogParts(raw)
  if (item.golaActiva && !parsed.hasGola) {
    return buildLinkedCatalogId({
      base: parsed.base,
      acabadoToken: parsed.acabadoToken,
      includeGola: true,
    })
  }
  return normalizeLinkedCatalogId(raw)
}

/**
 * Calcula el catalogID destino para un item concreto.
 * Orden válido: BASE [Ru|LA|LI|R2] GO — nunca BASE GO Ru ni BASE GO LI.
 */
export function resolveCatalogIdForAcabadoChange(
  currentCatalogId: string,
  textureAcabado: string,
): AcabadoUnitDecision | null {
  const current = String(currentCatalogId ?? '').trim()

  if (!current) {
    return { action: 'skip', reason: 'empty-catalog', from: current }
  }
  if (!unitAcceptsSpecialSuffix(current)) {
    return { action: 'skip', reason: 'unit-not-eligible', from: current }
  }

  const normalizedCurrent = normalizeLinkedCatalogId(current)

  // Código corrupto en store (ej. KAB4260 GO LI) → reparar antes de evaluar skip
  if (normalizedCurrent !== current) {
    return { action: 'apply', catalogId: normalizedCurrent, from: current }
  }

  const { base, hasGola } = parseLinkedCatalogParts(normalizedCurrent)
  const acabadoToken = normalizeAcabadoToken(getSuffixForAcabado(textureAcabado))

  if (isVitrinaCatalogId(base) && acabadoToken) {
    return { action: 'skip', reason: 'unit-not-eligible', from: current }
  }

  // Grupos sin sufijo: quitar LA/LI/Ru/R2 del GLB (mantener GO si aplica)
  if (!acabadoToken && textureAcabado) {
    const { acabadoToken: currentToken } = parseLinkedCatalogParts(normalizedCurrent)
    if (!currentToken && !hasGola) {
      return { action: 'skip', reason: 'already-base', from: current }
    }
    const strippedId = buildLinkedCatalogId({ base, acabadoToken: '', includeGola: hasGola })
    if (strippedId === normalizedCurrent) {
      return { action: 'skip', reason: 'already-base', from: current }
    }
    return { action: 'apply', catalogId: strippedId, from: current }
  }

  const newId = buildLinkedCatalogId({
    base,
    acabadoToken,
    includeGola: hasGola && acabadoToken !== 'LI',
  })

  if (newId === normalizedCurrent) {
    return { action: 'skip', reason: 'already-target', from: current }
  }

  return { action: 'apply', catalogId: newId, from: current }
}

export function buildCatalogIdForAcabadoChange(
  currentCatalogId: string,
  textureAcabado: string,
): string | null {
  const result = resolveCatalogIdForAcabadoChange(currentCatalogId, textureAcabado)
  return result?.action === 'apply' ? result.catalogId : null
}
